import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { useLanguage } from '../../contexts/LanguageContext';
import { quantiteACombler } from '../../utils/reappro';

/**
 * L'ajustement de stock — le dialogue qui montre le résultat AVANT de le produire.
 *
 * 🔴 CE QUE LE LOT RU3-b APPORTE, ET C'EST TOUT LE LOT : « après opération »
 * se met à jour PENDANT la saisie. Jusqu'ici on validait puis on découvrait le
 * résultat — c'est-à-dire qu'on corrigeait après coup une erreur qu'on aurait
 * vue avant. Trois chiffres en haut, le troisième vivant : actuel, seuil,
 * après. Le reste du dialogue existe pour alimenter ce troisième chiffre.
 *
 * 🔴 ET DEUX ONGLETS, PLUS DE `+/−`. Un écart signé demandait au client de
 * traduire son intention (« je reçois 12 ») en un signe. Entrée et Sortie sont
 * deux gestes du métier, pas deux signes d'un même nombre — et ils n'écrivent
 * d'ailleurs PAS la même chose côté serveur, cf. le motif ci-dessous.
 */

/**
 * ⚠️ LE « MOTIF » N'EST PAS LA MÊME COLONNE DES DEUX CÔTÉS, et ce n'est pas un
 * détail d'habillage : une entrée porte une `reference` (le bon de livraison),
 * une sortie une `reason` (vente, perte…). Mesuré en base — les 10 entrées ont
 * 10 références TOUTES DISTINCTES, de la forme `BL-9632` : c'est un identifiant,
 * il ne peut être qu'un champ libre. Les sorties, elles, tournent sur un petit
 * vocabulaire.
 *
 * 🔴 MAIS CE VOCABULAIRE NE MESURE RIEN. Les 17 sorties en base donnent
 * « Vente » ×6, « Perte » ×4, « Transfert » ×3, « Maintenance » ×2 — et ces
 * quatre valeurs sortent de `random.choice(['Vente', 'Transfert', 'Perte',
 * 'Maintenance'])`, la ligne 623 du seed de démonstration **supprimé au lot
 * R1**. La distribution mesure un tirage aléatoire dans une liste écrite à la
 * main, pas un usage. Les 2 seules sorties non issues du seed portent
 * « Ajustement via liste » — un littéral que le mini-formulaire écrivait
 * lui-même, donc pas davantage un choix d'utilisateur.
 *
 * **Il n'existe aucune donnée réelle sur ce champ.** Le critère « liste si les
 * valeurs se répètent, champ libre si elles varient » ne peut donc pas être
 * appliqué : il n'y a rien à départager. La liste reste OUVERTE — on propose
 * quatre motifs parce qu'ils ont du sens dans le métier, et on accepte
 * n'importe quoi d'autre. Fermer la liste maintenant, ce serait choisir le
 * vocabulaire du client à sa place, sur la foi d'un tirage aléatoire.
 *
 * ⚠️ ET LA VRAIE LIMITE, CONSIGNÉE PLUTÔT QUE CONTOURNÉE : ces motifs sont
 * traduits, donc **le même motif se stocke différemment selon la langue de
 * l'interface**. Tant que le motif n'est que du texte affiché, c'est sans
 * conséquence. Le jour où l'on voudra filtrer ou totaliser par motif, il faudra
 * un CODE et un libellé — le modèle des `*_STATUS_META` — et une migration.
 */
const MOTIFS_SORTIE = ['reasonSale', 'reasonLoss', 'reasonTransfer', 'reasonMaintenance'];

const DialogueAjustementStock = ({ produit, ouvert, enCours, onFermer, onValider }) => {
  const { t } = useLanguage();
  const [onglet, setOnglet] = useState('entree');
  const [quantite, setQuantite] = useState('');
  const [motif, setMotif] = useState('');

  // ⚠️ REMIS À ZÉRO SUR `ouvert` ET SUR LE PRODUIT. Sans la dépendance au
  // produit, fermer puis rouvrir sur un autre article conserverait la saisie du
  // précédent — un « 12 » destiné à une référence appliqué à une autre, sans
  // aucun signe. L'onglet revient à « entrée », qui est le geste le plus
  // fréquent (on reçoit plus souvent qu'on ne corrige).
  useEffect(() => {
    setOnglet('entree');
    setQuantite('');
    setMotif('');
  }, [ouvert, produit?.id]);

  const stockActuel = produit?.current_stock || 0;
  const seuil = produit?.min_stock || 0;
  const q = parseInt(quantite, 10);
  const quantiteValide = !Number.isNaN(q) && q > 0;
  const saisie = quantiteValide ? q : 0;

  const apres = onglet === 'entree' ? stockActuel + saisie : stockActuel - saisie;

  // 🔴 LA SORTIE EST BORNÉE ICI **ET** SUR LE SERVEUR — vérifié, pas supposé.
  // `create_stock_exit` appelle `process_stock_exit`, qui refuse en 400 dès que
  // `current_stock < quantity` (« Stock insuffisant. Stock actuel: N »), et la
  // route en lot passe par le même point. Les DEUX seuls appelants gardent.
  // ⚠️ Cette garde-ci n'est donc PAS la sécurité : elle est le confort. Elle
  // évite un aller-retour et une erreur rouge pour une chose que l'écran savait
  // déjà. Si elle disparaissait, un appel direct resterait refusé — c'est la
  // propriété qu'il fallait mesurer avant de la poser.
  const sortieTropGrande = onglet === 'sortie' && saisie > stockActuel;

  // ── Q1 : « COMBLER » EST ABSENT, PAS GRISÉ ────────────────────────────────
  // Sur un produit au-dessus du seuil, il n'a rien à combler. Un bouton grisé
  // fait chercher pourquoi ; un bouton absent ne pose pas la question.
  //
  // ⚠️ ET IL VISE LE SEUIL **+ 1**, ce qui n'est pas une coquetterie. Le stock
  // bas est défini côté serveur par `current_stock <= min_stock` — mesuré à
  // CINQ endroits concordants (`stock_utils`, `dashboard` ×2, `products`, et
  // `to_dict`). Combler jusqu'au seuil EXACT laisserait donc le produit en
  // alerte : on cliquerait « Combler » et la ligne resterait teintée. Un bouton
  // qui ne fait pas ce que son nom annonce est pire que pas de bouton.
  //
  // ⚠️ `seuil > 0` REPREND LA CONDITION DU SERVEUR : un seuil à zéro veut dire
  // « aucun seuil défini », pas « seuil atteint ». Sans elle, un produit sans
  // seuil se verrait proposer de combler jusqu'à 1.
  // ⚠️ LA FORMULE A QUITTÉ CE FICHIER pour `utils/reappro.js` : le bouton
  // « Produits en alerte » du formulaire de commande a besoin de la MÊME, et
  // le sélecteur ouvert depuis Produits proposait déjà une quantité
  // différente. Le raisonnement qui la justifie est parti avec elle.
  const aCombler = quantiteACombler({ current_stock: stockActuel, min_stock: seuil });

  const couleurApres = useMemo(() => {
    if (apres === 0) return 'error.main';
    if (seuil > 0 && apres <= seuil) return 'warning.main';
    return 'success.main';
  }, [apres, seuil]);

  const motifsProposes = useMemo(
    () => (onglet === 'sortie' ? MOTIFS_SORTIE.map((c) => t(`products.${c}`)) : []),
    [onglet, t],
  );

  if (!produit) return null;

  const validable = quantiteValide && !sortieTropGrande && !enCours;
  const ajouter = (n) => setQuantite(String((quantiteValide ? q : 0) + n));

  return (
    <Dialog open={ouvert} onClose={onFermer} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {t('products.adjustStock')}
        <Typography variant="body2" color="text.secondary">
          {produit.name}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pt: 0 }}>
        {/* ── LES TROIS CHIFFRES ────────────────────────────────────────────
            🔴 Le troisième est le lot. Les deux premiers sont là pour qu'il
            veuille dire quelque chose : « après = 4 » ne se juge pas sans le
            seuil à côté. */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1,
            textAlign: 'center',
            py: 2,
          }}
        >
          {[
            { libelle: t('products.currentStock'), valeur: stockActuel, couleur: 'text.primary' },
            { libelle: t('common.minStock'), valeur: seuil, couleur: 'text.secondary' },
            { libelle: t('products.afterOperation'), valeur: apres, couleur: couleurApres },
          ].map((c) => (
            <Box key={c.libelle}>
              <Typography variant="caption" color="text.secondary" display="block">
                {c.libelle}
              </Typography>
              <Typography variant="h5" fontWeight={700} sx={{ color: c.couleur }}>
                {c.valeur}
              </Typography>
            </Box>
          ))}
        </Box>

        <Tabs
          value={onglet}
          onChange={(e, v) => setOnglet(v)}
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          <Tab value="entree" label={t('products.entry')} disabled={enCours} />
          <Tab value="sortie" label={t('products.exit')} disabled={enCours} />
        </Tabs>

        <TextField
          label={t('common.quantity')}
          value={quantite}
          onChange={(e) => {
            // On ne garde que des chiffres : le signe n'a plus de sens, c'est
            // l'onglet qui porte le sens de l'opération.
            const v = e.target.value.replace(/[^0-9]/g, '');
            setQuantite(v);
          }}
          fullWidth
          autoFocus
          size="small"
          inputProps={{ inputMode: 'numeric' }}
          error={sortieTropGrande}
          helperText={sortieTropGrande ? t('products.exitExceedsStock') : ' '}
          disabled={enCours}
        />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mb: 2 }}>
          {/* ⚠️ LES RACCOURCIS AJOUTENT, ILS NE REMPLACENT PAS : « +10 +5 »
              donne 15. Remplacer obligerait à retaper pour 15, ce qui ferait du
              raccourci une liste de valeurs autorisées plutôt qu'un accélérateur. */}
          <ButtonGroup size="small" variant="outlined" disabled={enCours}>
            {[1, 5, 10, 50].map((n) => (
              <Button key={n} onClick={() => ajouter(n)}>{`+${n}`}</Button>
            ))}
          </ButtonGroup>
          {onglet === 'entree' && aCombler > 0 && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              onClick={() => setQuantite(String(aCombler))}
              disabled={enCours}
            >
              {t('products.fillToThreshold', { n: aCombler })}
            </Button>
          )}
        </Box>

        {onglet === 'sortie' ? (
          <Autocomplete
            freeSolo
            options={motifsProposes}
            inputValue={motif}
            onInputChange={(e, v) => setMotif(v)}
            disabled={enCours}
            renderInput={(params) => (
              <TextField
                {...params}
                label={`${t('products.reason')} (${t('products.optional')})`}
                size="small"
              />
            )}
          />
        ) : (
          <TextField
            label={`${t('products.reference')} (${t('products.optional')})`}
            value={motif}
            onChange={(e) => setMotif(e.target.value)}
            fullWidth
            size="small"
            disabled={enCours}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onFermer} disabled={enCours}>
          {t('common.cancel')}
        </Button>
        {/* 🔴 UN BOUTON QUI DIT CE QU'IL FAIT. « Valider » ne nomme aucune
            action : il valide quoi, et dans quel sens ? Le libellé porte
            l'opération, donc l'onglet actif reste lisible même sans regarder
            les onglets. */}
        <Button
          variant="contained"
          disabled={!validable}
          onClick={() => onValider({ type: onglet, quantite: q, motif: motif.trim() })}
        >
          {onglet === 'entree' ? t('products.saveEntry') : t('products.saveExit')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogueAjustementStock;
